import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CATEGORIES, POSTS, Post } from "@/mocks/gossip";

export default function SearchPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [kw, setKw] = useState("");

  const results = useMemo<Post[]>(() => {
    const k = kw.trim().toLowerCase();
    if (!k) return POSTS.slice(0, 3);
    return POSTS.filter((p) => {
      const hay = `${p.title}${p.summary}${p.category}${p.tag}${p.content.join("")}`.toLowerCase();
      return hay.includes(k);
    });
  }, [kw]);

  const doSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setKw(q);
  };

  const searchLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "九一吃瓜",
    url: "https://199695.cn/search",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://199695.cn/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 pt-28 md:pt-36 pb-14 md:pb-20">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(searchLd) }} />

      <div className="max-w-2xl">
        <h1 className="font-heading text-2xl md:text-4xl text-foreground-950">
          搜索新瓜
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600">
          输入关键词，快速查找你关心的热点现场与速递内容。
        </p>

        <form onSubmit={doSearch} className="mt-6">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="试试输入：音乐节、综艺、暖心…"
                className="w-full rounded-full border border-background-300 bg-background-50 pl-11 pr-4 py-3 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
              />
            </div>
            <button
              type="submit"
              className="rounded-full bg-primary-500 text-background-50 text-sm font-medium px-7 py-3 hover:bg-primary-600 transition whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>

        {/* 热搜词 */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">热搜：</span>
          {["音乐节", "暖心", "剪纸", "夜市", "电竞"].map((t) => (
            <button
              key={t}
              onClick={() => {
                setKw(t);
                setQ(t);
              }}
              className="rounded-full bg-background-100 text-foreground-600 text-xs px-3 py-1.5 hover:bg-accent-500 hover:text-background-50 transition whitespace-nowrap"
            >
              # {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <h2 className="text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
          {kw ? (
            <>
              “{kw}”的搜索结果
              <span className="text-sm font-normal text-foreground-400">共 {results.length} 条</span>
            </>
          ) : (
            <>
              <i className="ri-flashlight-line text-accent-500"></i> 热门速递推荐
            </>
          )}
        </h2>

        {results.length === 0 ? (
          <div className="rounded-3xl border border-background-200 bg-background-50 py-20 text-center">
            <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 text-2xl">
              <i className="ri-file-search-line"></i>
            </span>
            <p className="mt-4 text-foreground-600">没有找到相关速递，换个关键词试试吧。</p>
          </div>
        ) : (
          <div className="space-y-4">
            {results.map((p) => (
              <Link
                key={p.id}
                to={`/post/${p.slug}`}
                className="group grid grid-cols-1 sm:grid-cols-12 gap-4 rounded-3xl border border-background-200 bg-background-50 p-3 hover:border-primary-200 transition"
              >
                <div className="sm:col-span-3 rounded-2xl overflow-hidden h-36 sm:h-28">
                  <img
                    src={p.cover}
                    alt={`${p.title} - 搜索结果`}
                    title={p.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="sm:col-span-9 flex flex-col justify-center p-1 sm:pr-2">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-medium text-primary-600 bg-primary-50 rounded-md px-2 py-0.5">{p.category}</span>
                    <span className="text-xs text-foreground-400">{p.tag}</span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition">
                    {p.title}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
                    <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{p.views}</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line"></i>{p.date}</span>
                    <span className="flex items-center gap-1"><i className="ri-book-open-line"></i>{p.minutes}分钟</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* 栏目快捷入口 */}
      <div className="mt-12">
        <h2 className="text-base font-bold text-foreground-950 mb-4">按栏目浏览</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {CATEGORIES.map((c) => (
            <div key={c.id} className="rounded-2xl border border-background-200 bg-background-50 p-4 text-center">
              <span className="mx-auto w-11 h-11 flex items-center justify-center rounded-2xl bg-background-100 text-secondary-600">
                <i className={`${c.icon} text-lg`}></i>
              </span>
              <h3 className="mt-2 text-sm font-bold text-foreground-900">{c.name}</h3>
              <p className="mt-0.5 text-xs text-foreground-400 line-clamp-2">{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}